import type { ValueEvent } from '@adornis/base/utilTypes.js';
import '@adornis/calendar/calendar.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { BooleanAspect } from '@adornis/filter/aspect-schemas/BooleanAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { Registermethode, StudentType } from '@chancenwerk/db/enums/enums.js';
import { ChancenwerkUserFilter } from '@chancenwerk/db/filter/ChancenwerkUserFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-dropdown-search-selection.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { UserAPI } from '@chancenwerk/master-data-management/users/api/UserAPI.js';
import {
  EntryInformation,
  SchoolInformation,
  Student,
} from '@chancenwerk/master-data-management/users/entities/Student.js';
import { html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { DateTime } from 'luxon';
import blankoWordlist from '../../../../chancenraum/src/client/assets/blanko_wordlist.json';

const filteredBlankoWordlist = blankoWordlist.filter(word => !/[öüä]/i.test(word));

/**
 * @customElement campus-page-blanko-accounts
 */
@customElement('campus-page-blanko-accounts')
export class CampusPageBlankoAccounts extends CrPage {
  @state() private readonly _currentBlankoStudent = new RXController(this, this._createBlankoStudent());
  @state() private readonly _currentBlankoPasswort = new RXController(this, this._generatePhrase());
  @state() private readonly _savedPasswords = new RXController<Record<string, string>>(this, {});

  constructor() {
    super();
    this.permission = ChancenPermission.campus.CREATE_BLANKO;
    this.label = 'Blanko-Accounts';
    this.description =
      'Hier können Blanko-Accounts für den Campus angelegt werden. Diese Accounts werden automatisch nach 7 Tagen gelöscht.';
    this.stickyHeader = true;
  }

  override connectedCallback() {
    super.connectedCallback();
  }

  override content() {
    return html`<cr-flex horizontal space="sm" padding="xs">
      ${this._listBlankosCard()} ${this._createBlankoCard()}
    </cr-flex>`;
  }

  private _listBlankosCard() {
    return html`
      <cr-card ${css({ flexGrow: 1 })} padding="md" space="md">
        <chancen-component-card-title> Deine Blanko-Accounts </chancen-component-card-title>
        <cr-data-view
          ${css({ marginTop: this.spacing.s })}
          .options=${{
            class: Student,
            view: new View({
              filter: new ChancenwerkUserFilter({
                aspects: [
                  new StringAspect({ mode: 'equals', fieldName: 'createdBy', value: this._currentUser.value?._id }),
                  new BooleanAspect({ fieldName: 'blankoAccount', value: true }),
                ],
              }),
            }),
            operation: UserAPI.subscribeByFilter,
            onlyShowLastTranslation: true,
            customColumns: [
              {
                columnName: 'Username',
                fieldName: 'username',
              },
              {
                columnName: 'Passwort',
                customID: 'pass',
                renderItem: entityInstance => {
                  const saved = this._savedPasswords.value[entityInstance.username];
                  if (saved) {
                    return html`<cr-text>${saved}</cr-text>`;
                  }
                  return html`<cr-icon>lock</cr-icon>`;
                },
              },
              {
                columnName: 'Jgst.',
                fieldName: 'schoolInformation.jahrgangsstufe',
              },
              {
                columnName: 'angelegt am',
                fieldName: 'createdAt',
                renderItem: entityInstance => {
                  return html`<cr-text
                    >${DateTime.fromJSDate(entityInstance.createdAt).toFormat('dd.MM.yyyy')}</cr-text
                  >`;
                },
              },
              {
                customID: 'delete',
                renderItem: entityInstance => {
                  return html` <cr-button
                    icon="delete"
                    mode="icon"
                    @click=${() => entityInstance.remove()}
                  ></cr-button>`;
                },
              },
            ],
          } as DataViewOptions<typeof Student>}
        >
        </cr-data-view>
      </cr-card>
    `;
  }

  private _createBlankoCard() {
    return html`<cr-card space="sm" padding="lg" ${css({ flexShrink: 0, width: '400px' })}>
      <cr-flex crossaxis-center space="sm" ${css({ marginBottom: this.spacing.md })}>
        <cr-text bold>Blanko Anlegen</cr-text>
      </cr-flex>
      <cr-input
        readonly
        .value=${this._currentBlankoStudent.value.username}
        placeholder="Username"
        icon="refresh"
        @icon-click=${() => this._currentBlankoStudent.next(this._createBlankoStudent())}
      >
      </cr-input>
      <cr-input
        readonly
        .value=${this._currentBlankoPasswort.value}
        placeholder="Passwort"
        icon="refresh"
        @icon-click=${() => this._currentBlankoPasswort.next(this._generatePhrase())}
      >
      </cr-input>
      <cr-dropdown-selection
        clearable
        .value=${this._currentBlankoStudent.value.schuelertyp}
        placeholder="Schüler:innentyp"
        .selectables=${Object.values(StudentType)}
        @value-picked=${(e: ValueEvent<StudentType>) => (this._currentBlankoStudent.value.schuelertyp = e.detail.value)}
      ></cr-dropdown-selection>
      <cr-dropdown-selection
        clearable
        .value=${this._currentBlankoStudent.value.schoolInformation.jahrgangsstufe}
        placeholder="Jahrgangsstufe"
        .selectables=${new Array(13).fill(null).map((_, i) => i + 1)}
        @value-picked=${(e: ValueEvent<number>) =>
          (this._currentBlankoStudent.value.schoolInformation.jahrgangsstufe = e.detail.value)}
      ></cr-dropdown-selection>
      <cr-button
        ${css({ marginBottom: this.spacing.md })}
        @click=${async () => ChromiumDialog.waitFor(this._createBlank())}
      >
        Anlegen
      </cr-button>
      <cr-flex horizontal space="md">
        <cr-icon ${css({ color: this.colors.tone.error, fontSize: '26px' })}>warning</cr-icon>
        <cr-text>
          Bitte merke dir das Passwort für den Blanko-Account. Das Passwort wird nur solange du auf dieser Seite bist
          angezeigt. Beim nächsten Aufruf ist es aus Sicherheitsgründen nicht mehr einsehbar!
        </cr-text>
      </cr-flex>
    </cr-card>`;
  }

  private _generatePhrase(words: number = 2) {
    return (
      new Array(words)
        .fill(null)
        .map(() => filteredBlankoWordlist[Math.floor(Math.random() * filteredBlankoWordlist.length)])
        .join('') + Math.floor(Math.random() * 9)
    );
  }

  private async _createBlank() {
    const student = this._currentBlankoStudent.value;
    student.createdBy = this._currentUser.value?._id;
    await Student.createBlanko(student, this._currentBlankoPasswort.value)();
    this._savedPasswords.next({
      ...this._savedPasswords.value,
      [student.username]: this._currentBlankoPasswort.value,
    });
    await ChromiumSnackbar.show('Blanko-Account erstellt');

    // Generate a new set
    this._currentBlankoStudent.next(this._createBlankoStudent());
    this._currentBlankoPasswort.next(this._generatePhrase());
  }

  private _createBlankoStudent(): Student {
    const name = this._generatePhrase();
    return new Student({
      blankoAccount: true,
      username: name,
      vorname: name,
      nachname: '',
      createdAt: DateTime.now().toJSDate(),
      schuelertyp: StudentType.US,
      schoolInformation: new SchoolInformation({ jahrgangsstufe: 5 }),
      joinInformation: new EntryInformation({
        eintrittsdatum: DateTime.now(),
        campusRegisterDataSend: true,
        registermethode: Registermethode.MANUALLY,
      }),
    });
  }
}
